import React from 'react';
import { motion } from 'framer-motion';
import { FaHeart, FaVideo, FaUsers, FaClock, FaMapMarkerAlt, FaBan, FaSmile, FaStar } from 'react-icons/fa';

const WhyDateniteSection = () => {
  const benefits = [
    {
      icon: <FaVideo className="text-2xl" />,
      title: "Real Video Connections",
      description: "See and hear your potential matches before meeting. No catfishing, no surprises.",
      color: "from-romantic-blue to-romantic-purple"
    },
    {
      icon: <FaClock className="text-2xl" />,
      title: "No Endless Swiping",
      description: "Open for 3 hours a night, focused on quality conversations over quantity of swipes & messages that fade into nothing.",
      color: "from-romantic-purple to-romantic-pink"
    },
    {
      icon: <FaMapMarkerAlt className="text-2xl" />,
      title: "Meet in Real Life",
      description: "Skip the endless texting. Meet your matches at amazing venues in Bournemouth.",
      color: "from-romantic-pink to-yellow-400"
    },
    {
      icon: <FaUsers className="text-2xl" />,
      title: "Local Community",
      description: "Partnered with Bournemouth’s best venues to give you amazing date deals and exclusive discounts for datenite users.",
      color: "from-yellow-400 to-romantic-blue"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.1,
        staggerChildren: 0.08
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="pb-20 relative overflow-hidden w-full">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <motion.div
              className="inline-flex items-center gap-2 mb-4"
              whileHover={{ scale: 1.05 }}
            >
              <FaHeart className="text-romantic-pink text-2xl" />
              <span className="text-romantic-pink font-semibold text-lg">Why Choose datenite?</span>
            </motion.div>
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
              What's the point of
              <span className="block gradient-text">datenite?</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Packed with features no other dating app offers—video speed dates, venue-based matching, personal ads, and exclusive local discounts—datenite makes meeting easy. We move you from online to in-person faster, turning matches into meetups and meetups into memories.
            </p>
          </motion.div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center mb-20">
            {/* Left Side - Benefits */}
            <motion.div variants={itemVariants} className="space-y-8">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  className="flex items-start gap-4 group"
                  whileHover={{ x: 10 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <motion.div
                    className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${benefit.color} flex items-center justify-center text-white flex-shrink-0`}
                    whileHover={{ rotate: 360, scale: 1.1 }}
                    transition={{ duration: 0.6 }}
                  >
                    {benefit.icon}
                  </motion.div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-romantic-pink transition-colors">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Right Side - Illustration */}
            <motion.div
              variants={itemVariants}
              className="relative flex justify-center"
            >
              {/* Main Illustration Container */}
              <div className="relative w-80 h-80">
                {/* Background Circle */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-br from-romantic-purple to-romantic-pink rounded-full opacity-20"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                />

                {/* Couple Illustration */}
                <motion.div
                  className="absolute inset-8 bg-gradient-to-br from-romantic-blue to-romantic-purple rounded-full flex items-center justify-center"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity }}
                >
                  <div className="text-center">
                    <motion.div
                      className="text-6xl mb-4"
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      💕
                    </motion.div>
                    <p className="text-white font-semibold">Real Connections</p>
                    <p className="text-gray-300 text-sm">Under the stars</p>
                  </div>
                </motion.div>

                {/* Floating Hearts */}
                {[...Array(6)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-4 h-4 text-romantic-pink"
                    style={{
                      top: `${20 + (i * 10)}%`,
                      left: `${10 + (i * 15)}%`,
                    }}
                    animate={{
                      y: [0, -20, 0],
                      opacity: [0.3, 1, 0.3],
                      rotate: [0, 180, 360]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      delay: i * 0.5
                    }}
                  >
                    <FaHeart />
                  </motion.div>
                ))}

                {/* Stars */}
                {[...Array(8)].map((_, i) => (
                  <motion.div
                    key={`star-${i}`}
                    className="absolute w-2 h-2 bg-white rounded-full"
                    style={{
                      top: `${Math.random() * 100}%`,
                      left: `${Math.random() * 100}%`,
                    }}
                    animate={{
                      opacity: [0.3, 1, 0.3],
                      scale: [1, 1.5, 1]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: i * 0.3
                    }}
                  />
                ))}
              </div>
            </motion.div>
          </div>

          {/* Problem vs Solution */}
          <motion.div variants={itemVariants} className="mb-16">
            <div className="grid md:grid-cols-2 gap-8">
              {/* The Problem */}
              <motion.div
                className="glass rounded-2xl p-8 relative overflow-hidden"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-red-500 to-red-600 opacity-10" />
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-4">
                    <FaBan className="text-red-400 text-2xl" />
                    <h3 className="text-xl font-bold text-white">The Problem with Dating Apps</h3>
                  </div>
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-red-400 rounded-full" />
                      Endless swiping with no real connections
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-red-400 rounded-full" />
                      Fake profiles and catfishing
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-red-400 rounded-full" />
                      Conversations that never lead to meetings
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-red-400 rounded-full" />
                      Ghosting and time wasting
                    </li>
                  </ul>
                </div>
              </motion.div>

              {/* The Solution */}
              <motion.div
                className="glass rounded-2xl p-8 relative overflow-hidden"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-green-500 to-green-600 opacity-10" />
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-4">
                    <FaSmile className="text-green-400 text-2xl" />
                    <h3 className="text-xl font-bold text-white">The datenite Solution</h3>
                  </div>
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-400 rounded-full" />
                      Quality video speed dates every night
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-400 rounded-full" />
                      See the real person before meeting
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-400 rounded-full" />
                      Immediate meetups at partner venues
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-400 rounded-full" />
                      Exclusive discounts at partner venues
                    </li>
                  </ul>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Testimonial */}
          <motion.div
            variants={itemVariants}
            className="text-center"
          >
            <motion.div
              className="glass rounded-2xl p-8 max-w-2xl mx-auto relative overflow-hidden"
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-romantic-purple to-romantic-pink opacity-10" />
              <div className="relative z-10">
                <div className="flex justify-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <FaStar key={i} className="text-yellow-400 text-xl" />
                  ))}
                </div>
                <blockquote className="text-xl text-white font-medium mb-4 italic">
                  "Finally, a dating app that actually leads to real dates! I met my girlfriend through datenite's 
                  video speed dating, and we had our first date at The Cosy Club the same night."
                </blockquote>
                <cite className="text-romantic-pink font-semibold">
                  - James, 28, Bournemouth
                </cite>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyDateniteSection;
